/**
 * CAOS-1861 — the client for the `listViews` noun: the views a PERSON made, beside the ones a
 * package shipped.
 *
 * ## Why this file exists at all, which is not what the ticket said
 *
 * The ticket was filed BUCKET 1, "the metadata cannot express it", on the measurement that
 * `kernel/src/api/metadata-routes.ts` carries no create/update/delete verb for a list view. That is
 * true, and the conclusion drawn from it is not: the verbs are not metadata verbs and do not live
 * there. CAOS-1579 built the whole noun in `kernel/src/api/list-view-routes.ts` — `list`, `save`,
 * `clone`, `share`, `delete` and `promote`, with an owner, sharing, and an admin promotion path —
 * and the census-held `docs/agent-pack/kernel-api-surface.md` has recorded it as six routes all
 * along.
 *
 * The real gap was on this side: `grep -rn "listViews/" host/src` returned **nothing**. The Engine
 * offered every verb and the workspace called none of them, so the host read only
 * `ctx.meta.listViews` — the installed package set — and a person's own views were unreachable from
 * the screen. Not because the platform could not hold them; because no control asked.
 *
 * So this is a host change over routes that already exist, already enforce their own access, and
 * already return the personal and package sets together in one answer.
 *
 * ## The one rule this module exists to carry
 *
 * `canEdit` and `canDelete` come from the ROUTE, per view, and are never inferred here. The route's
 * own comment says why, about a package's shipped view: "Nobody edits it in place — an admin changes
 * it by deploying, and a person changes it by cloning. Saying so here is what stops a surface
 * offering an affordance that can only fail." A surface that worked out for itself which views are
 * editable would be a second opinion on a question the Engine has already answered, and the two
 * would drift. Shared TO you is not yours either: you may use and clone such a view, and renaming
 * or deleting it would take it away from everybody else it was shared with, so the route reports
 * both flags false and this module passes them straight through.
 */

type FetchImpl = typeof fetch;

/** Where a view came from. A package's view is shipped metadata; a personal one is somebody's row. */
export type ViewSource = 'package' | 'personal';

/**
 * One view a person may choose, as the Engine describes it.
 *
 * Mirrors `ViewSummary` in `list-view-routes.ts` deliberately rather than reshaping it: the fields
 * this surface needs are the fields the route already decided to report, and a translation layer
 * would be a place for the two to disagree about what `canEdit` means.
 */
export interface ListViewSummary {
  readonly key: string;
  readonly label: string;
  readonly source: ViewSource;
  /** Present only for a personal view — a package's view has no row and therefore no id. */
  readonly id?: string;
  readonly owner?: string;
  readonly shared?: boolean;
  /** Whether THIS caller may rename or edit it. A package's view is never editable by anyone. */
  readonly canEdit: boolean;
  readonly canDelete: boolean;
  /** The authored body, which is what a clone copies and what the surface reads columns from. */
  readonly body: unknown;
}

/**
 * The outcome of a list-view write.
 *
 * Shaped after `WriteOutcome` in `record-write.ts` but WITHOUT its `conflict` arm, and the absence
 * is deliberate rather than an omission. That arm exists because a record carries an optimistic-lock
 * version and a save can lose a race against another person's. A personal list view has one owner by
 * construction — the route resolves it by `ownerId` and answers "not yours" the same way it answers
 * "no such view" — so there is no second writer to lose a race to, and an arm nothing can produce
 * would be a branch no surface could ever exercise.
 */
export type ViewWriteOutcome =
  | { readonly status: 'ok'; readonly view: ListViewSummary }
  | { readonly status: 'error'; readonly message: string };

/** The outcome of a delete, which returns no view to show. */
export type ViewDeleteOutcome = { readonly status: 'ok' } | { readonly status: 'error'; readonly message: string };

const LIST_PATH = '/api/listViews/list';
const SAVE_PATH = '/api/listViews/save';
const CLONE_PATH = '/api/listViews/clone';
const DELETE_PATH = '/api/listViews/delete';

/**
 * One sentence for the person who pressed the control, from whatever the Engine said went wrong.
 *
 * The route's message is preferred because it is about THEIR action and already worded for them —
 * "a view with that name already exists" is worth more than any fallback this module could write.
 * A code is never shown: a correlation id helps nobody standing at the screen. Mirrors
 * `failureMessage` in `record-write.ts`.
 */
function failureMessage(env: unknown, fallback: string): string {
  const e = env as { error?: { message?: unknown; reason?: unknown } } | null;
  const message = e?.error?.message;
  if (typeof message === 'string' && message.trim() !== '') return message;
  const reason = e?.error?.reason;
  if (typeof reason === 'string' && reason.trim() !== '') return reason;
  return fallback;
}

/**
 * Read one view out of a route answer, FAIL-SOFT on anything malformed.
 *
 * A view missing a key or a label is dropped rather than shown, on the rule `metadata-source.ts`
 * applies to every projection it does: a control labelled `undefined` is worse than a control that
 * is not there. `canEdit`/`canDelete` default to FALSE on anything that is not literally `true`,
 * which is the safe direction — the failure of guessing wrong is an affordance that can only fail,
 * and the failure of being too cautious is a view somebody has to clone instead of rename.
 */
function readSummary(raw: unknown): ListViewSummary | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const key = typeof r['key'] === 'string' ? r['key'] : '';
  const label = typeof r['label'] === 'string' ? r['label'] : '';
  if (key === '' || label === '') return null;
  const source: ViewSource = r['source'] === 'personal' ? 'personal' : 'package';
  return {
    key,
    label,
    source,
    ...(typeof r['id'] === 'string' ? { id: r['id'] } : {}),
    ...(typeof r['owner'] === 'string' ? { owner: r['owner'] } : {}),
    ...(typeof r['shared'] === 'boolean' ? { shared: r['shared'] } : {}),
    canEdit: r['canEdit'] === true,
    canDelete: r['canDelete'] === true,
    body: r['body'],
  };
}

/**
 * Every parameter goes in the BODY, not the query string.
 *
 * `requireStringParam` reads the body first and falls back to the query, so both work — and the body
 * is the right one here because a `label` is free text a person typed. A query string would put
 * "Q3 — Björn's deals / 50%+" through URL encoding on the way out and leave the label's correctness
 * resting on that round trip, for no gain. `view` (the authored body a save carries) has to be in
 * the body anyway, so this also keeps one request shape rather than two.
 */
async function postView(
  path: string,
  payload: Record<string, unknown>,
  fallback: string,
  fetchImpl: FetchImpl,
): Promise<ViewWriteOutcome> {
  try {
    const res = await fetchImpl(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const env = (await res.json()) as { ok?: boolean; data?: unknown; error?: unknown };
    if (env?.ok === true) {
      const view = readSummary(env.data);
      // OK-but-unreadable is an ERROR here, not a silent success. The caller's next act is to show
      // the saved view, and reporting success with nothing to show would leave the screen claiming
      // a view exists that it cannot name.
      if (view) return { status: 'ok', view };
      return { status: 'error', message: fallback };
    }
    return { status: 'error', message: failureMessage(env, fallback) };
  } catch {
    return { status: 'error', message: fallback };
  }
}

/**
 * Every view this person can use on one object — the package's and their own, in that order.
 *
 * The ORDER is the route's and is not re-sorted here. Its own reasoning: a package's views "are the
 * org's shipped answer to 'how do people usually look at this', and a personal view is an addition
 * to that rather than a replacement for it". Sorting alphabetically here would quietly overturn
 * that decision in the one place a reader would not look for it.
 *
 * Fail-soft to an EMPTY LIST rather than throwing. The caller draws a list screen, and a list screen
 * whose view chooser could not be read is still a list screen — it falls back to the installed set
 * it already has in `ctx.meta`, which is exactly what it showed before this existed.
 */
export async function listViewsFor(
  objectKey: string,
  fetchImpl: FetchImpl = fetch,
): Promise<readonly ListViewSummary[]> {
  try {
    const res = await fetchImpl(`${LIST_PATH}?object=${encodeURIComponent(objectKey)}`, {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    });
    const env = (await res.json()) as { ok?: boolean; data?: { views?: unknown } };
    if (env?.ok !== true || !Array.isArray(env.data?.views)) return [];
    return env.data.views.map(readSummary).filter((v): v is ListViewSummary => v !== null);
  } catch {
    return [];
  }
}

/**
 * Create or rename one of this person's OWN views.
 *
 * ONE VERB FOR BOTH, because that is what the route is: `save` is create-or-edit keyed by
 * `(object, key)`. New supplies a fresh key and the body the screen is currently showing; Rename
 * supplies the existing key and the existing body with a new label. Two client functions over one
 * route would imply a distinction the Engine does not make, and the first time they diverged one of
 * them would be wrong.
 */
export function saveView(
  objectKey: string,
  key: string,
  label: string,
  body: unknown,
  fetchImpl: FetchImpl = fetch,
): Promise<ViewWriteOutcome> {
  return postView(
    SAVE_PATH,
    { object: objectKey, key, label, view: body },
    'That view could not be saved just now. Try again.',
    fetchImpl,
  );
}

/**
 * Copy a view this person can SEE into one they own.
 *
 * `from` is the SOURCE's id for a personal view and its key for a shipped one, which is what
 * `resolveCloneSource` accepts. The body is NOT sent: the route reads the source itself, because a
 * client rebuilding the body from what it happened to render "would quietly drop any field the
 * surface does not draw, and the copy would differ from the view it claims to be a copy of". That
 * is the route's reason for existing rather than leaving this to a read-and-post, so this function
 * must not undo it by helpfully passing a body.
 */
export function cloneView(
  objectKey: string,
  from: string,
  key: string,
  label: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ViewWriteOutcome> {
  return postView(
    CLONE_PATH,
    { object: objectKey, from, key, label },
    'That view could not be copied just now. Try again.',
    fetchImpl,
  );
}

/**
 * Delete one of this person's own views, BY ID.
 *
 * By id and not by key, because that is the only thing that identifies a personal view's row — and
 * it is also why a package's view can never reach this route: it has no row at all, so "a package's
 * view cannot be deleted" is a property of where the two live rather than a check somebody has to
 * remember to write. A caller with no `id` therefore has a package view in hand and must not be
 * offering Delete in the first place; `canDelete` already said so.
 */
export async function deleteView(viewId: string, fetchImpl: FetchImpl = fetch): Promise<ViewDeleteOutcome> {
  const fallback = 'That view could not be deleted just now. Try again.';
  try {
    const res = await fetchImpl(DELETE_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ view: viewId }),
    });
    const env = (await res.json()) as { ok?: boolean; error?: unknown };
    if (env?.ok === true) return { status: 'ok' };
    return { status: 'error', message: failureMessage(env, fallback) };
  } catch {
    return { status: 'error', message: fallback };
  }
}

/**
 * A key for a NEW personal view, derived from the label the person typed.
 *
 * Personal views are keyed per owner per object, so this does not have to be globally unique — but
 * it does have to be a `ComponentRef`-shaped identifier, because the route stores it as a component
 * key and the body it saves is validated as list-view metadata. A label of "Q3 — Big deals!" has to
 * become something that shape admits.
 *
 * A SUFFIX IS APPENDED FROM THE EXISTING SET rather than from a clock or a random number. Both of
 * those would make the key unpredictable, and a key is what the address bar carries: two people
 * sharing a link to "my pipeline" should land on the same view, and a link should survive the
 * session that made it. `taken` is the keys already in use for this object, so the first free one
 * wins and a re-created view with the same name gets its name back.
 */
export function viewKeyFor(label: string, taken: Iterable<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'view';
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
  // A thousand views of one object named the same thing is not a state to design for, but silently
  // returning a colliding key would overwrite one of them, so the last resort still differs.
  return `${base}_${used.size + 1}`;
}
